import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

const ResumeVersions = ({ resume, onVersionsChange }) => {
  const [newLabel, setNewLabel] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  if (!resume) return null;

  const versions = resume.versions || [];

  const handleAddVersion = async () => {
    const label = newLabel.trim();
    if (!label) {
      toast.error("Please enter a version label (e.g. Frontend, AI, DevOps)");
      return;
    }

    setAdding(true);
    try {
      const formData = new FormData();
      formData.append("label", label);
      if (newFile) {
        formData.append("resume", newFile);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/resumes/${resume._id}/versions`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const listResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/resumes/${resume._id}/versions`
      );
      const updatedVersions = listResponse.data.versions || [
        ...versions,
        response.data,
      ];
      onVersionsChange(updatedVersions);
      setNewLabel("");
      setNewFile(null);
      toast.success(`Added "${label}" resume version`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add resume version");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteVersion = async (versionId) => {
    setDeletingId(versionId);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/resumes/${resume._id}/versions/${versionId}`
      );
      onVersionsChange(versions.filter((v) => v._id !== versionId));
      toast.success("Resume version deleted");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete resume version");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Resume Versions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add role-focused variants for this profile (e.g. Frontend, AI, DevOps). Pick which
        version to apply with in the browser extension.
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>PDF</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <strong>Default</strong>
              </TableCell>
              <TableCell>
                {resume.path ? (
                  <Button
                    size="small"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={() =>
                      window.open(
                        `${process.env.REACT_APP_API_URL}/resumefiles/${resume.path}`,
                        "_blank"
                      )
                    }
                  >
                    View
                  </Button>
                ) : (
                  "No file"
                )}
              </TableCell>
              <TableCell />
            </TableRow>
            {versions.map((version) => (
              <TableRow key={version._id}>
                <TableCell>{version.label}</TableCell>
                <TableCell>
                  {version.path ? (
                    <Button
                      size="small"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() =>
                        window.open(
                          `${process.env.REACT_APP_API_URL}/resumefiles/${version.path}`,
                          "_blank"
                        )
                      }
                    >
                      View
                    </Button>
                  ) : (
                    "Uses default PDF"
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleDeleteVersion(version._id)}
                    disabled={deletingId === version._id}
                  >
                    {deletingId === version._id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 2, flexWrap: "wrap" }}>
        <TextField
          size="small"
          label="Version label"
          placeholder="e.g. Frontend-focused"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          sx={{
            minWidth: 200,
            "& .MuiOutlinedInput-root": {
              height: 36,
            },
          }}
        />
        <Button
          variant="outlined"
          component="label"
          size="small"
          sx={{ height: 36, whiteSpace: "nowrap" }}
        >
          {newFile ? newFile.name : "Upload PDF"}
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            hidden
            onChange={(e) => setNewFile(e.target.files?.[0] || null)}
          />
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={adding ? <CircularProgress size={16} /> : <AddIcon />}
          onClick={handleAddVersion}
          disabled={adding}
          sx={{ height: 36 }}
        >
          Add Version
        </Button>
      </Box>
    </Paper>
  );
};

ResumeVersions.propTypes = {
  resume: PropTypes.object,
  onVersionsChange: PropTypes.func.isRequired,
};

export default ResumeVersions;
